import React, { useState, useEffect } from 'react';
import { Card, Form, Row, Col, Select, Button, Spin, DatePicker } from 'antd';
import {
    districtBranchWiseDeposits,
    districtDistrictWiseDeposits,
    districtDivisionalWiseDeposits
} from './SavingService';
import { errHandler } from '../../helper/helper';
import { disabledDate } from "../../Utils/Util";
import {
    exportExcelBranchWise,
    exportExcelDistrictWise,
    exportExcelDivisionalWise
} from "../../Utils/DistrictAndBranchDepositsExport";

const { Option, OptGroup } = Select;

const DistrictReport = ({
    loginPlaceId,
    products,
    districtData,
    districtName,
    divisionalId,
    branchId
}) => {

    const [selectedDistricts, setSelectedDistricts] = useState([]);
    const [selectedDivisionalAreas, setSelectedDivisionalAreas] = useState([]);
    const [selectedBranches, setSelectedBranches] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);


    const [availableDivisionalAreas, setAvailableDivisionalAreas] = useState([]);
    const [availableBranches, setAvailableBranches] = useState([]);


    const [loading, setLoading] = useState(false);
    const [selectionScope, setSelectionScope] = useState(""); // "district" | "divisional" | "branch"


    // Update available branches based on selected districts
    useEffect(() => {
        let filteredDivisionalAreas = [];

        if (loginPlaceId === "2" && divisionalId) {
            // Divisional-level user: find the matching divisional area from any district
            filteredDivisionalAreas = districtData
                .flatMap(d => d.divisionalAreaDtoList || [])
                .filter(div => div.divAId === divisionalId);

            setSelectedDivisionalAreas([divisionalId]); // auto-select the one they're assigned to
        } else {
            // Normal case: based on selected districts
            filteredDivisionalAreas = districtData
                .filter(d => selectedDistricts.includes(d.districtId))
                .flatMap(d => d.divisionalAreaDtoList || []);

            // Clean up selected divisional areas
            setSelectedDivisionalAreas(prev =>
                prev.filter(did =>
                    filteredDivisionalAreas.some(d => d.divAId === did)
                )
            );
        }

        setAvailableDivisionalAreas(filteredDivisionalAreas); // <- you should set this too

    }, [selectedDistricts, districtData, loginPlaceId, divisionalId]);

    //update available branches based on selected divisional areas
    useEffect(() => {
        const filteredBranches = districtData
            .flatMap(d => d.divisionalAreaDtoList || [])
            .filter(div => selectedDivisionalAreas.includes(div.divAId))
            .flatMap(div => div.branchDtoList || []);

        setAvailableBranches(filteredBranches);

        // Clean up selected branches
        setSelectedBranches(prev =>
            prev.filter(bid =>
                filteredBranches.some(b => b.id === bid)
            )
        );
    }, [selectedDivisionalAreas, districtData]);


    const generateBranchWiseReport = async () => {
        let data = {
            branchIdList: loginPlaceId === "1" ? [branchId] : selectedBranches,
            productIdList: selectedProducts,
            selectedDate: selectedDate
        }
        try {
            const res = await districtBranchWiseDeposits(data);
            return res; // ✅ Now you're returning it from getDistrictData
        } catch (err) {
            errHandler(err);
            throw err; // optionally rethrow so caller knows it failed
        }
    }
    const generateDistrictWiseReport = async () => {
        let data = {
            districtIdList: selectedDistricts,
            productIdList: selectedProducts,
            selectedDate: selectedDate
        }
        try {
            const res = await districtDistrictWiseDeposits(data);
            return res; // ✅ Now you're returning it from getDistrictData
        } catch (err) {
            errHandler(err);
            throw err; // optionally rethrow so caller knows it failed
        }
    }
    const generateDivisionalWiseReport = async () => {
        let data = {
            divisionalIdList: selectedDivisionalAreas,
            productIdList: selectedProducts,
            selectedDate: selectedDate
        }
        try {
            const res = await districtDivisionalWiseDeposits(data);
            return res; // ✅ Now you're returning it from getDistrictData
        } catch (err) {
            errHandler(err);
            throw err; // optionally rethrow so caller knows it failed
        }
    }

    const handleExport = async () => {
        setLoading(true);
        try {

            const effectiveScope = loginPlaceId === "1" ? 'branch' : selectionScope;
            const reportGenerators = {
                district: generateDistrictWiseReport,
                divisional: generateDivisionalWiseReport,
                branch: generateBranchWiseReport,
            };

            const exporters = {
                district: exportExcelDistrictWise,
                divisional: exportExcelDivisionalWise,
                branch: exportExcelBranchWise,
            };

            const generateReport = reportGenerators[effectiveScope];
            const exportExcel = exporters[effectiveScope];

            if (!generateReport || !exportExcel) {
                console.warn("Invalid selection scope.");
                return;
            }

            const reportDataFetch = await generateReport();
            const hasData = reportDataFetch.data.length > 0;

            if (hasData) {
                await exportExcel({
                    apiData: reportDataFetch.data,
                    fileName: `District_and_Branch_Deposits_${selectedDate.format("YYYY-MM-DD")}.xlsx`,
                    loginPlaceId:loginPlaceId
                });
            } else {
                console.warn("No data available to export.");
            }
        } catch (error) {
            console.error("Export failed:", error);
        } finally {
            setLoading(false);
        }
    };



    return (
        <div className="panel-container">
            <Card hoverable className="inner-card">
                <Form layout="vertical">
                    <Row gutter={[16, 16]}>
                        {/* District auto complete */}
                        {(loginPlaceId === "3" || loginPlaceId === "4" || loginPlaceId === "2") && (

                        <Col span={24}>
                            <Form.Item label="Select Scope">
                                <Select
                                    placeholder="Select scope"
                                    value={selectionScope}
                                    onChange={setSelectionScope}
                                    style={{ width: '100%' }}
                                >
                                    {(loginPlaceId === "3" || loginPlaceId === "4") && (
                                        <Option value="district">District-wise</Option>)}
                                    {(loginPlaceId === "3" || loginPlaceId === "4" || loginPlaceId === "2") && (
                                        <Option value="divisional">Divisional-wise</Option>)}
                                    {(loginPlaceId === "3" || loginPlaceId === "4" || loginPlaceId === "2" || loginPlaceId === "1") && (
                                        <Option value="branch">Branch-wise</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                        )}

                        {(loginPlaceId === "3" || loginPlaceId === "4") && (
                            <Col span={24}>
                                <Form.Item label="Select Districts">
                                    <Select
                                        mode="multiple"
                                        placeholder="Select districts"
                                        style={{ width: '100%' }}
                                        value={selectedDistricts}
                                        onChange={setSelectedDistricts}
                                    >
                                        {districtData
                                            .filter(d => loginPlaceId === "4" ? d.districtName === districtName : true)
                                            .map(d => (
                                                <Option key={d.districtId} value={d.districtId}>
                                                    🏙️ {d.districtName}
                                                </Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}
                        {(loginPlaceId === "3" || loginPlaceId === "4" || loginPlaceId === "2") && (
                        <Col span={24}>
                            <Form.Item label="Select Divisional Areas">
                                <Select
                                    mode="multiple"
                                    placeholder="Select divisional areas"
                                    style={{ width: '100%' }}
                                    value={selectedDivisionalAreas}
                                    onChange={setSelectedDivisionalAreas}
                                    disabled={selectionScope !== 'divisional' && selectionScope !== 'branch'}
                                >
                                    {availableDivisionalAreas
                                        // .filter(d => loginPlaceId === "2" ? d.divAId === divisionalId : true)
                                        .map(d => (
                                            <Option key={d.divAId} value={d.divAId}>
                                                🏙️ {d.divAIdName}
                                            </Option>
                                        ))}
                                </Select>
                            </Form.Item>
                        </Col>)}
                        {(loginPlaceId === "3" || loginPlaceId === "4" || loginPlaceId === "2") && (
                        <Col span={24}>
                            <Form.Item label="Select Branch(es)">
                                <Select
                                    mode="multiple"
                                    placeholder="Select branches"
                                    style={{ width: '100%' }}
                                    value={selectedBranches}
                                    onChange={(values) => {
                                        if (values.includes('ALL')) {
                                            setSelectedBranches(availableBranches.map(b => b.id));
                                        } else {
                                            setSelectedBranches(values);
                                        }
                                    }}
                                    disabled={selectionScope !== 'branch' || availableBranches.length === 0}
                                >
                                    <Option key="ALL" value="ALL">
                                        🔘 Select All Branches
                                    </Option>
                                    {availableBranches.map(branch => (
                                        <Option key={branch.id} value={branch.id}>
                                            🏢 {branch.branchName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        )}

                        <Col span={24}>
                            <Form.Item label="Select Date">
                                <DatePicker onChange={(date) => setSelectedDate(date)} disabledDate={disabledDate} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>

                        {/* Multi-select product */}
                        <Col span={24}>
                            <Form.Item label="Select Products">
                                <Select
                                    mode="multiple"
                                    allowClear
                                    placeholder="Select products"
                                    style={{ width: '100%' }}
                                    value={selectedProducts}
                                    onChange={(values) => {
                                        if (values.includes('ALL')) {
                                            setSelectedProducts(products.map(b => b.productId));
                                        } else {
                                            setSelectedProducts(values);
                                        }
                                    }}
                                // onChange={setSelectedProducts}
                                >
                                    <Option key="ALL" value="ALL">
                                        🔘 Select All Products
                                    </Option>
                                    {products.map(product => (
                                        <Option key={product.productId} value={product.productId}>
                                            {product.productName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        {/* Submit button */}
                        <Col span={24}>
                            <Form.Item className="generate-btn-outline">
                                <Button
                                    onClick={handleExport}
                                    disabled={loading}
                                    className="generate-btn"
                                    style={{ textAlign: 'right' }}
                                >
                                    {loading ? <Spin /> : 'Generate Report'}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>
        </div>
    );
};

export default DistrictReport;
